import {
	DesktopEntityTypeWithSystems,
	DesktopJobEntityType,
	DesktopSystemEntityType,
	DesktopSystemEntityTypeInsert,
	LocationEntityType,
	LocationEntityTypeInsert,
	PrinterConfigEntityType,
	PrinterConfigEntityTypeInsert,
} from "@/lib/supabase/supabaseTypes";
import { SupabaseTableEnum } from "@/lib/supabase/supabaseTypes";
import { StateCreator } from "zustand";
import { dateOlderThanSeconds } from "../../lib/utils/utils-functions";
import { supabase } from "@/lib/supabase";
import { Logger } from "@/lib/logger/Logger";
import { produce } from "immer";
import { DesktopSystemRequestTypes, PrintJobItem, State } from "./types";
import type { PrinterCapabilities } from "./types";
import { initialState } from "./utils";
import { DesktopSystemTypeEnum } from "@/lib/supabase/supabaseEnums";
import { showNotification } from "../Central/selectors";

const VISIBLE_DESKTOP_JOB_TYPES = [
	DesktopSystemRequestTypes.PrintDocument,
	DesktopSystemRequestTypes.PrintLabels,
	DesktopSystemRequestTypes.ScanDocument,
];

export interface DesktopSystemEntityTypeWithStatus
	extends DesktopSystemEntityType {
	active: boolean;
	desktopName: string;
}

interface Actions {
	fetchDesktops: (organizationId: string) => Promise<void>;
	addDesktopSystem: (
		system: DesktopSystemEntityTypeInsert
	) => Promise<null | number>;
	deleteDesktop: (desktopId: string) => Promise<void>;
	softDeleteDesktop: (desktopId: string) => Promise<void>;
	deleteDesktopSystem: (desktopSystemId: number) => Promise<void>;
	fetchDesktopSystem: (desktopSystemId: string) => Promise<void>;
	updateDesktopSystem: (
		desktopSystemId: number,
		system: Partial<DesktopSystemEntityType>
	) => Promise<void>;
	fetchRequests: (organizationId: string, cursor: number) => Promise<void>;
	getPrintersWithStatus: () => DesktopSystemEntityTypeWithStatus[];
	getSystemIdToDesktopIdLookup: () => Record<number, string>;
	fetchLocations: (organizationId: string) => Promise<void>;
	createLocation: (
		location: LocationEntityTypeInsert
	) => Promise<LocationEntityType | null>;
	updateLocation: (
		locationId: number,
		updates: Partial<LocationEntityType>
	) => Promise<void>;
	deleteLocation: (locationId: number) => Promise<void>;
	setDefaultLocation: (
		locationId: number,
		organizationId: string
	) => Promise<void>;
	fetchPrinterConfigs: (organizationId: string) => Promise<void>;
	upsertPrinterConfig: (
		config: PrinterConfigEntityTypeInsert
	) => Promise<void>;
	deletePrinterConfig: (id: number) => Promise<void>;
	getConfigsForLocation: (locationId: number) => PrinterConfigEntityType[];
	getOrgPrintMode: (locationId: number) => "browser" | "desktop";
	getAutoprint: (locationId: number, configKey: string) => boolean;
	getPrinterCapabilities: (
		desktopSystemId: number
	) => PrinterCapabilities | null;
	postponeUpdate: (desktopId: string, installAt: string) => Promise<void>;
	fetchPrintJobs: (organizationId: string) => Promise<void>;
	retryPrintJob: (job: PrintJobItem) => Promise<void>;
}

export type DesktopsSlice = State & Actions;

export const createDesktopsStore: StateCreator<DesktopsSlice> = (set, get) => ({
	...initialState,

	fetchDesktops: async (organizationId: string) => {
		const { data, error } = await supabase
			.from("desktops")
			.select("*, desktop_systems!left (*)")
			.eq("organization_id", organizationId)
			.is("deleted_at", null)
			.is("desktop_systems.deleted_at", null);
		if (error) {
			Logger.error(error);
			return;
		}
		set({
			desktopsWithSystems: (data as DesktopEntityTypeWithSystems[]) ?? [],
		});
	},

	addDesktopSystem: async (
		system: DesktopSystemEntityTypeInsert
	): Promise<null | number> => {
		const { data, error } = await supabase
			.from(SupabaseTableEnum.DESKTOP_SYSTEMS)
			.insert([system])
			.select("*");
		if (error) {
			Logger.error(error);
			showNotification({
				message: "Fehler beim Hinzufügen des Systems",
				type: "error",
			});
			return null;
		}
		set((state) => ({
			desktopsWithSystems: state.desktopsWithSystems.map((desktop) => {
				if (desktop.id === data[0]?.desktop_id) {
					return {
						...desktop,
						desktop_systems: [...desktop.desktop_systems, data[0]],
					};
				}
				return desktop;
			}),
		}));
		return data[0]?.id;
	},

	deleteDesktop: async (desktopId: string) => {
		await supabase
			.from(SupabaseTableEnum.DESKTOPS)
			.delete()
			.eq("id", desktopId);
		set((state) => ({
			desktopsWithSystems: state.desktopsWithSystems.filter(
				(desktop) => desktop.id !== desktopId
			),
		}));
	},

	softDeleteDesktop: async (desktopId: string) => {
		const { error } = await supabase
			.from(SupabaseTableEnum.DESKTOPS)
			.update({ deleted_at: new Date().toISOString() })
			.eq("id", desktopId);
		if (error) {
			Logger.error(error);
			showNotification({
				message: "Fehler beim Entfernen des Desktops",
				type: "error",
			});
			return;
		}
		set((state) => ({
			desktopsWithSystems: state.desktopsWithSystems.filter(
				(desktop) => desktop.id !== desktopId
			),
		}));
		showNotification({ message: "Desktop entfernt", type: "success" });
	},

	deleteDesktopSystem: async (desktopSystemId: number) => {
		const { error } = await supabase
			.from(SupabaseTableEnum.DESKTOP_SYSTEMS)
			.update({ deleted_at: new Date().toISOString() })
			.eq("id", desktopSystemId);
		if (error) {
			Logger.error(error);
			showNotification({
				message: "Fehler beim Entfernen des Druckers",
				type: "error",
			});
			return;
		}
		set((state) => ({
			desktopsWithSystems: state.desktopsWithSystems.map((desktop) => ({
				...desktop,
				desktop_systems: desktop.desktop_systems.filter(
					(system) => system.id !== desktopSystemId
				),
			})),
		}));
		showNotification({ message: "Drucker entfernt", type: "success" });
	},

	fetchDesktopSystem: async (desktopSystemId: string) => {
		const systemId = Number(desktopSystemId);
		const { data: desktopSystem, error: desktopSystemError } =
			await supabase
				.from(SupabaseTableEnum.DESKTOP_SYSTEMS)
				.select()
				.eq("id", systemId);

		if (desktopSystemError) {
			Logger.error(desktopSystemError);
			return;
		}

		const { data: desktopJobs, error: desktopJobsError } = await supabase
			.from("desktop_jobs")
			.select("*")
			.eq("desktop_system_id", systemId)
			.in("type", VISIBLE_DESKTOP_JOB_TYPES)
			.order("created_at", { ascending: false })
			.limit(20);

		if (desktopJobsError) {
			Logger.error(desktopJobsError);
			return;
		}

		set({
			selectedDesktopSystem: {
				...desktopSystem[0],
				desktop_jobs: desktopJobs as DesktopJobEntityType[],
			},
		});
	},

	updateDesktopSystem: async (
		desktopSystemId: number,
		system: Partial<DesktopSystemEntityType>
	) => {
		set((state) => ({
			desktopsWithSystems: state.desktopsWithSystems.map((desktop) => {
				return {
					...desktop,
					desktop_systems: desktop.desktop_systems.map(
						(desktopSystem) => {
							if (desktopSystem.id === desktopSystemId) {
								return {
									...desktopSystem,
									...system,
								};
							}
							return desktopSystem;
						}
					),
				};
			}),
		}));

		const { error } = await supabase
			.from(SupabaseTableEnum.DESKTOP_SYSTEMS)
			.update(system)
			.eq("id", desktopSystemId);

		if (error) {
			Logger.error(error);
			return;
		}
	},

	fetchRequests: async (organizationId: string, cursor: number) => {
		const { data, error } = await supabase
			.from("desktop_jobs")
			.select("*, desktop_systems(*,desktops(*))")
			.eq("desktop_systems.desktops.organization_id", organizationId)
			.in("type", VISIBLE_DESKTOP_JOB_TYPES)
			.order("created_at", { ascending: false })
			.range(cursor, cursor + 19);

		if (error) {
			Logger.error(error);
			return;
		}

		set({
			desktopRequests: data.filter(
				(
					d
				): d is typeof d & {
					desktop_systems: NonNullable<typeof d.desktop_systems>;
				} => d.desktop_systems !== null
			),
		});
	},

	getPrintersWithStatus: () => {
		const { desktopsWithSystems } = get();
		if (!desktopsWithSystems) {
			return [];
		}
		const printersWithStatus = desktopsWithSystems.flatMap((desktop) =>
			!dateOlderThanSeconds(new Date(desktop.last_active_at), 60)
				? desktop.desktop_systems
						.filter(
							(system) =>
								system.type === DesktopSystemTypeEnum.PRINTER
						)
						.map((system) => ({
							...system,
							active: true,
							desktopName: desktop.name,
						}))
				: desktop.desktop_systems
						.filter(
							(system) =>
								system.type === DesktopSystemTypeEnum.PRINTER
						)
						.map((system) => ({
							...system,
							active: false,
							desktopName: desktop.name,
						}))
		);
		return printersWithStatus;
	},

	getSystemIdToDesktopIdLookup: () => {
		const lookup: Record<number, string> = get().desktopsWithSystems.reduce(
			(acc, desktop) => {
				const newAcc = desktop.desktop_systems.reduce(
					(acc, system) => ({
						...acc,
						[system.id]: desktop.id,
					}),
					{}
				);
				return { ...acc, ...newAcc };
			},
			{}
		);
		Logger.log(
			"SystemId to DesktopId lookup",
			get().desktopsWithSystems,
			lookup
		);
		return lookup;
	},

	fetchLocations: async (organizationId: string) => {
		const { data, error } = await supabase
			.from("locations")
			.select("*")
			.eq("organization_id", organizationId)
			.order("sort_order", { ascending: true });
		if (error) {
			Logger.error(error);
			return;
		}
		set({ locations: data as LocationEntityType[] });
	},

	createLocation: async (location: LocationEntityTypeInsert) => {
		const { data, error } = await supabase
			.from("locations")
			.insert(location)
			.select()
			.single();
		if (error) {
			Logger.error(error);
			showNotification({
				message: "Fehler beim Erstellen des Standorts",
				type: "error",
			});
			return null;
		}
		set(
			produce((state: State) => {
				state.locations.push(data as LocationEntityType);
			}) as any
		);
		showNotification({ message: "Standort erstellt", type: "success" });
		return data as LocationEntityType;
	},

	updateLocation: async (
		locationId: number,
		updates: Partial<LocationEntityType>
	) => {
		const { id: _, ...updateData } = updates as any;
		const { error } = await supabase
			.from("locations")
			.update(updateData)
			.eq("id", locationId);
		if (error) {
			Logger.error(error);
			showNotification({
				message: "Fehler beim Aktualisieren des Standorts",
				type: "error",
			});
			return;
		}
		set(
			produce((state: State) => {
				const idx = state.locations.findIndex(
					(l: LocationEntityType) => l.id === locationId
				);
				if (idx !== -1) Object.assign(state.locations[idx], updates);
			}) as any
		);
	},

	deleteLocation: async (locationId: number) => {
		const { error } = await supabase
			.from("locations")
			.delete()
			.eq("id", locationId);
		if (error) {
			Logger.error(error);
			showNotification({
				message: "Fehler beim Löschen des Standorts",
				type: "error",
			});
			return;
		}
		set(
			produce((state: State) => {
				state.locations = state.locations.filter(
					(l: LocationEntityType) => l.id !== locationId
				);
				state.printerConfigs = state.printerConfigs.filter(
					(c: PrinterConfigEntityType) => c.location_id !== locationId
				);
			}) as any
		);
		showNotification({ message: "Standort gelöscht", type: "success" });
	},

	setDefaultLocation: async (locationId: number, organizationId: string) => {
		const { error: unsetError } = await supabase
			.from("locations")
			.update({ is_default: false })
			.eq("organization_id", organizationId)
			.eq("is_default", true);
		if (unsetError) {
			Logger.error(unsetError);
			showNotification({
				message: "Fehler beim Ändern des Standard-Standorts",
				type: "error",
			});
			return;
		}
		const { error: setError } = await supabase
			.from("locations")
			.update({ is_default: true })
			.eq("id", locationId);
		if (setError) {
			Logger.error(setError);
			showNotification({
				message: "Fehler beim Ändern des Standard-Standorts",
				type: "error",
			});
			return;
		}
		set(
			produce((state: State) => {
				for (const loc of state.locations) {
					loc.is_default = loc.id === locationId;
				}
			}) as any
		);
		showNotification({
			message: "Standard-Standort geändert",
			type: "success",
		});
	},

	fetchPrinterConfigs: async (organizationId: string) => {
		const { data, error } = await supabase
			.from("printer_configs")
			.select("*")
			.eq("organization_id", organizationId);
		if (error) {
			Logger.error(error);
			return;
		}
		set({ printerConfigs: (data as PrinterConfigEntityType[]) ?? [] });
	},

	upsertPrinterConfig: async (config: PrinterConfigEntityTypeInsert) => {
		const { data, error } = await supabase
			.from("printer_configs")
			.upsert(config, {
				onConflict: "organization_id,location_id,config_key",
			})
			.select()
			.single();
		if (error) {
			Logger.error(error);
			showNotification({
				message: "Fehler beim Speichern der Druckerkonfiguration",
				type: "error",
			});
			return;
		}
		set(
			produce((state: State) => {
				const idx = state.printerConfigs.findIndex(
					(c: PrinterConfigEntityType) =>
						c.organization_id ===
							(data as PrinterConfigEntityType).organization_id &&
						c.location_id ===
							(data as PrinterConfigEntityType).location_id &&
						c.config_key ===
							(data as PrinterConfigEntityType).config_key
				);
				if (idx !== -1) {
					state.printerConfigs[idx] = data as PrinterConfigEntityType;
				} else {
					state.printerConfigs.push(data as PrinterConfigEntityType);
				}
			}) as any
		);
	},

	deletePrinterConfig: async (id: number) => {
		const { error } = await supabase
			.from("printer_configs")
			.delete()
			.eq("id", id);
		if (error) {
			Logger.error(error);
			return;
		}
		set(
			produce((state: State) => {
				state.printerConfigs = state.printerConfigs.filter(
					(c: PrinterConfigEntityType) => c.id !== id
				);
			}) as any
		);
	},

	getConfigsForLocation: (locationId: number) => {
		return get().printerConfigs.filter((c) => c.location_id === locationId);
	},

	getOrgPrintMode: (locationId: number) => {
		const settings = get().printerConfigs.find(
			(c) =>
				c.location_id === locationId && c.config_key === "_org_settings"
		);
		if (
			settings?.print_mode === "browser" ||
			settings?.print_mode === "desktop"
		) {
			return settings.print_mode;
		}
		return get().desktopsWithSystems.length > 0 ? "desktop" : "browser";
	},

	getAutoprint: (locationId: number, configKey: string) => {
		const config = get().printerConfigs.find(
			(c) => c.location_id === locationId && c.config_key === configKey
		);
		return config?.auto_print ?? false;
	},

	postponeUpdate: async (desktopId: string, installAt: string) => {
		set((state) => ({
			desktopsWithSystems: state.desktopsWithSystems.map((d) =>
				d.id === desktopId ? { ...d, postpone_update_to: installAt } : d
			),
		}));
		const { error } = await supabase
			.from("desktops")
			.update({ postpone_update_to: installAt })
			.eq("id", desktopId);
		if (error) {
			Logger.error(error);
			showNotification({
				message: "Fehler beim Verschieben des Updates",
				type: "error",
			});
		}
	},

	getPrinterCapabilities: (desktopSystemId: number) => {
		const { desktopsWithSystems } = get();
		for (const desktop of desktopsWithSystems) {
			const system = desktop.desktop_systems.find(
				(s) => s.id === desktopSystemId
			);
			if (system?.config) {
				const config = system.config as Record<string, unknown>;
				if (
					Array.isArray(config.trays) ||
					Array.isArray(config.paperSizes)
				) {
					return config as unknown as PrinterCapabilities;
				}
			}
		}
		return null;
	},

	fetchPrintJobs: async (organizationId: string) => {
		const { data, error } = await supabase
			.from("desktop_jobs")
			.select(
				"*, desktop_systems(name), desktops!inner(name, organization_id)"
			)
			.eq("desktops.organization_id", organizationId)
			.in("type", [2, 3])
			.order("created_at", { ascending: false })
			.limit(50);
		if (error) {
			Logger.error(error);
			return;
		}
		set({
			printJobs: (data ?? []).map((item: any) => ({
				id: item.id,
				created_at: item.created_at,
				type: item.type,
				data: item.data,
				desktop_system_id: item.desktop_system_id,
				desktop_id: item.desktop_id,
				status: item.status,
				response_status: item.response_status,
				response_message: item.response_message,
				processed_at: item.processed_at,
				printer_name: item.desktop_systems?.name ?? "\u2013",
				desktop_name: item.desktops?.name ?? "\u2013",
			})),
		});
	},

	retryPrintJob: async (job) => {
		const { error } = await supabase.from("desktop_jobs").insert({
			type: job.type,
			data: job.data,
			desktop_id: job.desktop_id,
			desktop_system_id: job.desktop_system_id,
		});
		if (error) {
			showNotification({
				message: "Fehler beim Erstellen des Druckauftrags",
				type: "error",
			});
			return;
		}
		showNotification({
			message: "Druckauftrag erneut gesendet",
			type: "success",
		});
		const orgId = get().desktopsWithSystems[0]?.organization_id;
		if (orgId) get().fetchPrintJobs(orgId);
	},
});
